import { useState, useEffect } from 'react';
import { Pencil, Save, X, Trophy, Link as LinkIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useOrganizationStore } from '../../store/organizationStore';
import { BlastSchoolTypeahead } from '../common';
import type { OrganizationWithPeople, OrganizationUpdate, BlastSchool } from '../../types';
import { orgTypeLabel, blastEntityId } from '../../lib/orgTypes';

interface OrganizationCRMFieldsProps {
  organization: OrganizationWithPeople;
  /**
   * Gates the Edit button and disables form inputs in edit mode.
   * Defaults to true to preserve existing callsites. NOTE: the backend
   * PATCH endpoint has no per-org role gate today (Task 1.2 finding), so
   * this is a UX cue rather than a security boundary.
   */
  canEdit?: boolean;
}

const DIVISION_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'D1', label: 'Division I (D1)' },
  { value: 'D2', label: 'Division II (D2)' },
  { value: 'D3', label: 'Division III (D3)' },
  { value: 'NAIA', label: 'NAIA' },
  { value: 'JUCO', label: 'JUCO' },
  { value: 'Other', label: 'Other' },
];

/**
 * CRM-editable fields card
 * Allows editing conference, division, and Blast School ID
 */
export const OrganizationCRMFields = ({ organization, canEdit = true }: OrganizationCRMFieldsProps) => {
  const { updateOrganization, isLoadingDetail } = useOrganizationStore();

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [conference, setConference] = useState(organization.conference || '');
  const [division, setDivision] = useState(organization.division || '');
  const [blastSchoolId, setBlastSchoolId] = useState(organization.blastSchoolId || '');
  const [selectedBlastSchool, setSelectedBlastSchool] = useState<BlastSchool | null>(null);

  // Reset form when organization changes
  useEffect(() => {
    setConference(organization.conference || '');
    setDivision(organization.division || '');
    setBlastSchoolId(organization.blastSchoolId || '');
    setSelectedBlastSchool(null);
  }, [organization]);

  // Handle cancel - reset to original values
  const handleCancel = () => {
    setConference(organization.conference || '');
    setDivision(organization.division || '');
    setBlastSchoolId(organization.blastSchoolId || '');
    setSelectedBlastSchool(null);
    setIsEditing(false);
  };

  // Handle blast school selection from typeahead
  const handleBlastSchoolChange = (school: BlastSchool | null) => {
    setSelectedBlastSchool(school);
    setBlastSchoolId(school ? String(school.id) : '');
  };

  // Handle save
  const handleSave = async () => {
    try {
      const updates: OrganizationUpdate = {};

      // Only include changed fields
      if (conference !== (organization.conference || '')) {
        updates.conference = conference || null;
      }
      if (division !== (organization.division || '')) {
        updates.division = division || null;
      }
      if (blastSchoolId !== (organization.blastSchoolId || '')) {
        updates.blastSchoolId = blastSchoolId || null;
      }

      // Skip if nothing changed
      if (Object.keys(updates).length === 0) {
        setIsEditing(false);
        return;
      }

      await updateOrganization(organization.id, updates);
      toast.success('Organization updated successfully');
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update organization');
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          CRM Information
        </h2>
        {!isEditing ? (
          canEdit && (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-secondary text-sm"
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </button>
          )
        ) : (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCancel}
              disabled={isLoadingDetail}
              className="btn-secondary text-sm"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoadingDetail}
              className="btn-primary text-sm"
            >
              <Save className="h-4 w-4 mr-1" />
              {isLoadingDetail ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
        These fields are managed by Blast CRM and can be edited
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Conference */}
        <div>
          <label className="label mb-1">
            <div className="flex items-center">
              <Trophy className="h-4 w-4 mr-1 text-gray-400" />
              Athletic Conference
            </div>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={conference}
              onChange={(e) => setConference(e.target.value)}
              placeholder="e.g., Pacific Coast League"
              className="input"
              disabled={isLoadingDetail}
            />
          ) : (
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {organization.conference || <span className="text-gray-400 dark:text-gray-500">Not specified</span>}
            </p>
          )}
        </div>

        {/* Division */}
        <div>
          <label className="label mb-1">
            <div className="flex items-center">
              <Trophy className="h-4 w-4 mr-1 text-gray-400" />
              Division
            </div>
          </label>
          {isEditing ? (
            <select
              value={division}
              onChange={(e) => setDivision(e.target.value)}
              className="input"
              disabled={isLoadingDetail}
            >
              {DIVISION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {organization.division || <span className="text-gray-400 dark:text-gray-500">Not specified</span>}
            </p>
          )}
        </div>

        {/* Blast School ID */}
        <div className="md:col-span-2">
          <label className="label mb-1">
            <div className="flex items-center">
              <LinkIcon className="h-4 w-4 mr-1 text-gray-400" />
              Blast {orgTypeLabel(organization.type)} ID
            </div>
          </label>
          {isEditing && organization.type === 'SCHOOL' ? (
            <div>
              <BlastSchoolTypeahead
                value={selectedBlastSchool}
                onChange={handleBlastSchoolChange}
                placeholder="Search Blast schools..."
                disabled={isLoadingDetail}
              />
              {!selectedBlastSchool && organization.blastSchoolId && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Currently linked to ID: {organization.blastSchoolId} — search to change
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {blastEntityId(organization) ? (
                <span className="font-mono">{blastEntityId(organization)}</span>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">Not linked to Blast</span>
              )}
            </p>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Links this organization to Blast for data sync
          </p>
        </div>
      </div>
    </div>
  );
};
